import { localeMap, type Language } from '@/i18n';

const DOW_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DOW_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** ISO "yyyy-MM-dd" → a local Date at midnight, free of timezone drift. */
export function parseISODate(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00`);
}

/** Date → ISO "yyyy-MM-dd" in local time (never toISOString, which shifts to UTC). */
export function toISODate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayISO(): string {
  return toISODate(new Date());
}

/** Monday of the ISO week containing the given day — matches the backend's PrazoPolicy. */
export function startOfWeek(iso: string): string {
  const d = parseISODate(iso);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return toISODate(d);
}

export function addDays(iso: string, days: number): string {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/**
 * Whole weeks from the Monday of `de` to the Monday of `ate` — negative when
 * `ate` is the earlier of the two. Rounded, so a DST week still counts as one.
 */
export function weeksBetween(de: string, ate: string): number {
  const a = parseISODate(startOfWeek(de)).getTime();
  const b = parseISODate(startOfWeek(ate)).getTime();
  return Math.round((b - a) / (7 * 24 * 60 * 60 * 1000));
}

export function isWeekend(iso: string): boolean {
  const day = parseISODate(iso).getDay();
  return day === 0 || day === 6;
}

/** "2026-08" for the month containing an ISO date. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

export function addMonths(mes: string, delta: number): string {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Hours as the crew writes them: "8h", "2h30". Never "8.5" — half hours are the
 * unit everyone speaks in, and a decimal point reads as a typo on a payslip.
 */
export function formatHoras(horas: number | null | undefined): string {
  if (!horas) return '0h';
  const whole = Math.floor(horas);
  const minutes = Math.round((horas - whole) * 60);
  return minutes ? `${whole}h${String(minutes).padStart(2, '0')}` : `${whole}h`;
}

/** "Seg 25/08" — the compact day label used across the app. */
export function formatDiaCurto(iso: string, lang: Language = 'pt'): string {
  const d = parseISODate(iso);
  const dow = (lang === 'pt' ? DOW_PT : DOW_EN)[d.getDay()];
  return `${dow} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "Agosto 2026" */
export function formatMes(mes: string, lang: Language = 'pt'): string {
  const [y, m] = mes.split('-').map(Number);
  const label = new Intl.DateTimeFormat(localeMap[lang], { month: 'long', year: 'numeric' })
    .format(new Date(y, m - 1, 1));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Two-letter initials for the avatar circles. */
export function initials(nome: string): string {
  return nome
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function firstName(nome: string): string {
  return nome.split(' ')[0] ?? nome;
}

/** Percentage of a target, clamped to [0, 999] so a wild value can't break a bar. */
export function pct(value: number, target: number): number {
  if (!target) return 0;
  return Math.min(999, Math.round((value / target) * 100));
}
